import React, { useCallback, useMemo, useState } from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  SvgIcon,
  Tooltip,
  Typography,
} from '@mui/material';
import ModeIcon from '@mui/icons-material/Mode';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CategoryIcon from '@mui/icons-material/Category';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PhoneIcon from '@mui/icons-material/Phone';
import BlockIcon from '@mui/icons-material/Block';
import MarkAsUnreadIcon from '@mui/icons-material/MarkAsUnread';
import PhoneSelector from 'src/views/management/ChatView/PhoneSelector';
import { buildContactPhoneList } from 'src/views/management/ChatView/contactPhones';
import ModalForms from 'src/views/management/ChatView/ChatDetails/ModalForms';
import BookingJoiningModal from 'src/components/BookingJoiningModal';
import { clearFilteredChatMessages, getFilteredChatMessages, markChatAsUnread } from 'src/actions/chatActions';
import {
  setBackToChatsList,
  setBackToPartyGuestList,
  setBackToCustomerList,
} from 'src/actions/customersActions';
import {
  callFromCP,
  updateCustomer,
} from 'src/actions/customerDetailsActions';
import { PERMISSIONS_SYSTEM_CODE } from 'src/constants/permissions';
import { ReactComponent as BackButtonIcon } from 'src/assets/images/back-button.svg';
import {
  CONTACTS,
  ROUTES,
  STYLE_OPTIONS,
} from 'src/constants';
import helpers from 'src/helpers';

const { CONTACT_MODAL_FORM_NAMES } = CONTACTS;

function Toolbar() {
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    loading: contactLoading,
    customer: contact,
  } = useSelector((state) => state?.customerDetails || {});
  const { messagesFilteredByPhone, chats } = useSelector((state) => state?.chat || {});
  const {
    cpVoiceCallingEnabled,
    textingProvider,
    userRole,
  } = useSelector((state) => state?.account?.user || {});
  const { permissions: userRolePermissions } = userRole || {};
  const [openModal, setOpenModal] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const {
    id: contactId,
    phone1,
    phone2,
    phone3,
    firstName,
    lastName,
    email,
    doNotText,
  } = contact || {};

  // Check if CP voice calling is available
  const isTextingProviderCP = useMemo(() => textingProvider === 'CP', [textingProvider]);
  const isCpVoiceCallingAccess = useMemo(
    () => helpers.permissions.doesUserHavePermission(
      PERMISSIONS_SYSTEM_CODE.CP_VOICE_CALLING_ACCESS,
      userRolePermissions
    ),
    [userRolePermissions]
  );
  const isCpVoiceCallingEnabled = useMemo(
    () => !!(
      isTextingProviderCP
      && cpVoiceCallingEnabled
      && !contactLoading
      && !helpers.isEmpty(contactId)
      && !helpers.isEmpty(phone1)
    ),
    [cpVoiceCallingEnabled, contactLoading, contactId, phone1, isTextingProviderCP]
  );
  const showCallUsingCPNumber = isTextingProviderCP && isCpVoiceCallingAccess;
  const hasBookingJoiningPermission = useMemo(
    () => helpers.permissions.doesUserHavePermission(
      PERMISSIONS_SYSTEM_CODE.CONTACT_BOOKING_JOINING,
      userRolePermissions
    ),
    [userRolePermissions]
  );
  const isOnlyOnePhoneNumber = useMemo(
    () => isCpVoiceCallingEnabled && helpers.isEmpty(phone2) && helpers.isEmpty(phone3),
    [isCpVoiceCallingEnabled, phone2, phone3]
  );

  // CON-1705 — built by the shared helper so this list and the one the
  // "Send to:" selector uses cannot drift apart; PhoneSelector matches a phone
  // to its info by index.
  const {
    phones: contactPhones,
    phoneInfoList: contactPhoneInfoList,
  } = buildContactPhoneList(contact);

  // Get current chat's unread count for Mark as Unread feature
  const chatKey = contactId ? String(contactId) : null;
  const currentChat = chatKey ? chats?.byKey?.[chatKey] : null;
  const chatUnreadCount = currentChat?.unreadCount || 0;

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleVideoOpen = () => {
    setOpenModal('ModalDialogVideo');
  };

  const handleChangeFilterByPhone = (e) => {
    const filterByPhone = e.target.value;
    if (filterByPhone) {
      dispatch(getFilteredChatMessages({
        contactId,
        phone: filterByPhone,
      }));
    } else {
      dispatch(clearFilteredChatMessages());
    }
  };

  const setCopyValuesToBack = () => {
    dispatch(setBackToCustomerList());
    dispatch(setBackToPartyGuestList());
    dispatch(setBackToChatsList({ active: true }));
  };

  const onClickContactDetails = useCallback((id) => history.push(`${ROUTES.APP_MANAGEMENT_CUSTOMERS}/${id}`), [history]);

  const handleClickViewContact = () => {
    handleMenuClose();
    if (contactId) {
      setCopyValuesToBack();
      onClickContactDetails(contactId);
    }
  };

  const handleClickAddCategory = () => {
    handleMenuClose();
    setOpenModal('ModalAddCategory');
  };

  const handleClickAddToGroup = () => {
    handleMenuClose();
    setOpenModal('ModalAddToGroup');
  };

  const handleClickExecuteKeyword = () => {
    handleMenuClose();
    setOpenModal('ModalExecuteKeyword');
  };

  const handleClickBookingJoining = () => {
    handleMenuClose();
    setOpenModal('ModalBookingJoining');
  };

  const handleClickCallUsingCPNumber = () => {
    handleMenuClose();
    if (isOnlyOnePhoneNumber) {
      // Show "Calling you at..." modal immediately before API call starts.
      // If the call is rejected (e.g. CP Voice Calling disabled or no
      // forwarding number), close the modal so it doesn't contradict the
      // error snackbar by claiming the call is happening.
      setOpenModal(CONTACT_MODAL_FORM_NAMES.MODAL_CALLING_YOU_TO_CONNECT_YOU_WITH);
      dispatch(callFromCP(contactId, phone1, false, () => setOpenModal('')));
      return;
    }
    setOpenModal(CONTACT_MODAL_FORM_NAMES.MODAL_SELECT_NUMBER_TO_CALL);
  };

  const handleClickDoNotText = () => {
    handleMenuClose();
    // Pass all required fields like mobile app does (see Contact.save in mobile models)
    dispatch(updateCustomer(contactId, {
      id: contactId,
      firstName,
      lastName,
      phone1,
      phone2,
      phone3,
      email,
      doNotText: true,
    }));
  };

  const handleClickMarkAsUnread = () => {
    handleMenuClose();
    const doWhenTry = () => {
      history.push(ROUTES.APP_MANAGEMENT_CHAT);
    };
    dispatch(markChatAsUnread(chatKey, doWhenTry));
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: 64,
          flexShrink: 0,
          padding: '10px 16px',
        }}
      >
        <Box
          sx={{
            display: {
              xs: 'flex',
              md: 'none',
            },
          }}
        >
          <Tooltip
            title="Return to Connections list"
            placement="right-start"
          >
            <Button
              component={Link}
              to={ROUTES.APP_MANAGEMENT_CHAT}
              sx={{
                ...STYLE_OPTIONS.BUTTON_EMPTY_STYLE,
                '&:hover svg': STYLE_OPTIONS.SVG_COLOR_BLUE
              }}
            >
              <SvgIcon
                className="svgIcon"
                component={BackButtonIcon}
                viewBox="0 0 28 28"
              />
            </Button>
          </Tooltip>
        </Box>
        {!helpers.isEmpty(contact) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: { xs: '6px', md: '36px' },
              marginRight: { xs: '6px', md: '24px' },
            }}
          >
            <Typography
              variant="h3"
              color="textPrimary"
              onClick={handleClickViewContact}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              {helpers.transform.arrayToStringWithoutEmpty([firstName, lastName], ' ')}
            </Typography>
          </Box>
        )}
        {(!helpers.isEmpty(contactPhones) && contactPhones.length > 1) && (
          <PhoneSelector
            isToolbarSelector
            value={messagesFilteredByPhone}
            options={contactPhones}
            phoneInfoList={contactPhoneInfoList}
            onChange={handleChangeFilterByPhone}
          />
        )}
        {!helpers.isEmpty(contact) && (
          <>
            <Box
              sx={{
                marginLeft: 'auto',
              }}
            />
            <Tooltip
              title="How to use Connections screen"
              placement="left-start"
            >
              <IconButton
                color="secondary"
                onClick={handleVideoOpen}
                sx={{
                  width: '40px',
                  minWidth: '40px',
                }}
              >
                <OndemandVideoIcon />
              </IconButton>
            </Tooltip>
            <IconButton
              color="secondary"
              aria-label="Chat actions"
              onClick={handleMenuOpen}
              sx={{
                width: '40px',
                minWidth: '40px',
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleClickExecuteKeyword}>
                <ListItemIcon>
                  <VpnKeyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Execute Keyword</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleClickAddCategory}>
                <ListItemIcon>
                  <CategoryIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Categories</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleClickAddToGroup}>
                <ListItemIcon>
                  <GroupAddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add to Group</ListItemText>
              </MenuItem>
              {hasBookingJoiningPermission && (
                <MenuItem onClick={handleClickBookingJoining}>
                  <ListItemIcon>
                    <EventAvailableIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Booking / Joining</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={handleClickViewContact}>
                <ListItemIcon>
                  <ModeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>View Contact</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={handleClickMarkAsUnread}
                disabled={chatUnreadCount > 0}
              >
                <ListItemIcon>
                  <MarkAsUnreadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Mark as unread</ListItemText>
              </MenuItem>
              {showCallUsingCPNumber && (
                <MenuItem
                  onClick={handleClickCallUsingCPNumber}
                  disabled={!isCpVoiceCallingEnabled}
                >
                  <ListItemIcon>
                    <PhoneIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Call Using CP Number</ListItemText>
                </MenuItem>
              )}
              <MenuItem
                onClick={handleClickDoNotText}
                disabled={doNotText}
              >
                <ListItemIcon>
                  <BlockIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Do Not Ever Text</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
      <ModalForms
        onOpenModal={setOpenModal}
        openModal={openModal}
        loading={contactLoading}
        contact={contact}
      />
      <BookingJoiningModal
        isVisible={openModal === 'ModalBookingJoining'}
        onClose={() => setOpenModal('')}
        contact={contact}
      />
    </>
  );
}

export default Toolbar;
